import React, { useState, useEffect } from 'react';
import { Search, Download, File, Image, Video, AlertCircle, CheckCircle, FolderOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFileViewer = () => {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);

  // Auto-load order ID from URL parameter
  useEffect(() => {
    const orderParam = searchParams.get('order') || searchParams.get('search');
    if (orderParam) {
      setOrderId(orderParam);
      // Auto-trigger search after a brief delay
      setTimeout(() => {
        handleSearchWithId(orderParam);
      }, 500);
    }
  }, [searchParams]);

  const handleSearchWithId = async (searchOrderId) => {
    const id = searchOrderId || orderId;
    if (!id.trim()) {
      toast.error('Please enter an Order ID');
      return;
    }

    setLoading(true);
    setError(null);
    setOrderData(null);

    try {
      const response = await axios.get(`${API}/download-files/${id.trim()}`);
      if (response.data.success) {
        setOrderData(response.data);
        toast.success(`Found ${response.data.file_count} files!`);
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.response?.status === 404) {
        setError('No files found for this Order ID. Customer may not have uploaded files yet.');
      } else {
        setError('Error loading files. Please check the Order ID and try again.');
      }
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    handleSearchWithId(orderId);
  };

  const handleDownload = (file) => {
    // Backend expects the file UUID, NOT the original filename.
    // download_url from the API already includes the leading "/api/...", so we
    // must combine it with BACKEND_URL (which is the origin only) — NOT with API
    // (which is BACKEND_URL + "/api") or we'd end up with "/api/api/...".
    const url = file.download_url
      ? `${BACKEND_URL}${file.download_url}`
      : `${API}/download-file/${orderId}/${file.id}`;
    window.open(url, '_blank');
    toast.success(`Downloading ${file.filename}`);
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <Image size={20} className="text-blue-400" />;
    } else if (['mp4', 'mov', 'avi', 'wmv'].includes(ext)) {
      return <Video size={20} className="text-purple-400" />;
    } else {
      return <File size={20} className="text-gray-400" />;
    }
  };

  const getTotalSize = () => {
    if (!orderData?.files) return 0;
    return orderData.files.reduce((sum, file) => sum + file.size_mb, 0).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-black pt-24 md:pt-40 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              📂 Admin File Viewer
            </h1>
            <p className="text-gray-400">
              Enter an Order ID from Stripe to view and download customer files
            </p>
          </div>

          {/* Search Box */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 mb-8">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Enter Order ID (e.g., abc12345)"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={loading}
                className={`px-8 py-3 rounded-lg font-semibold flex items-center gap-2 ${
                  loading
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-600 hover:to-amber-700'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    Search
                  </>
                )}
              </button>
            </div>

            {/* Quick Guide */}
            <div className="mt-4 p-4 bg-cyan-900/20 rounded-lg border border-cyan-500/30">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold mb-1">How to find Order ID:</p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-400">
                    <li>Go to Stripe Dashboard → Payments</li>
                    <li>Click on a payment</li>
                    <li>Scroll to "Metadata" section</li>
                    <li>Copy the "order_id" value</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-500 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-3">
                <AlertCircle size={24} className="text-red-400" />
                <div>
                  <h3 className="text-red-400 font-semibold mb-1">Error</h3>
                  <p className="text-gray-300">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {orderData && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="bg-gradient-to-br from-green-900/20 to-green-800/10 border border-green-500 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle size={24} className="text-green-400" />
                  <h2 className="text-2xl font-bold text-white">Order Found!</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Order ID</p>
                    <p className="text-white font-mono">{orderData.order_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Files</p>
                    <p className="text-white font-semibold">{orderData.file_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Size</p>
                    <p className="text-white font-semibold">{getTotalSize()} MB</p>
                  </div>
                  <div>
                    <button
                      onClick={() => {
                        orderData.files.forEach(file => handleDownload(file));
                      }}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
                    >
                      Download All
                    </button>
                  </div>
                </div>
              </div>

              {/* Files List */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center gap-2">
                    <FolderOpen size={24} className="text-amber-400" />
                    <h3 className="text-xl font-bold text-white">Uploaded Files</h3>
                  </div>
                </div>

                <div className="divide-y divide-gray-700">
                  {orderData.files.map((file, index) => (
                    <div
                      key={index}
                      className="p-4 hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {getFileIcon(file.filename)}
                          <div className="flex-1 min-w-0">
                            <p className="text-white truncate">{file.filename}</p>
                            <p className="text-gray-400 text-sm">{file.size_mb} MB</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(file)}
                          className="ml-4 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-lg hover:from-amber-600 hover:to-amber-700 flex items-center gap-2 font-semibold"
                        >
                          <Download size={18} />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!orderData && !error && !loading && (
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700 p-12 text-center">
              <FolderOpen size={64} className="mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                Enter an Order ID to view files
              </h3>
              <p className="text-gray-500">
                Get the Order ID from Stripe payment metadata
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFileViewer;
